import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Avatar,
  Snackbar,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { keyframes } from "@mui/system";
import TypingEffect from "../viewers/TypingEffect";
import DataTable from "../viewers/DataTable";
import DynamicChartViewer from "../viewers/DynamicChartViewer";
import DownloadIcon from "@mui/icons-material/Download";
import TrendingUp from "@mui/icons-material/TrendingUp";
import BarChart from "@mui/icons-material/BarChart";
import MonetizationOn from "@mui/icons-material/MonetizationOn";
import Person from "@mui/icons-material/Person";
import Speed from "@mui/icons-material/Speed";

import { renameColumn, updateColumnType } from "../../services/api";

const fadeIn = keyframes`
  from {opacity:0;transform:translateY(10px);}
  to {opacity:1;transform:translateY(0);}
`;

// Data types list for dropdown
const DATA_TYPES = [
  "integer",
  "string",
  "number",
  "float",
  "boolean",
  "date",
  "datetime",
  "currency",
  "percentage",
  "object",
  "array",
];

// Simplified KPI Card
function KpiCard({ title, value, trend, colorIndex = 0, darkMode }) {
  const kpiColors = [
    {
      light: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      dark: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
    },
    {
      light: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      dark: "linear-gradient(135deg, #e53e3e 0%, #c53030 100%)",
    },
    {
      light: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      dark: "linear-gradient(135deg, #3182ce 0%, #2b6cb0 100%)",
    },
    {
      light: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      dark: "linear-gradient(135deg, #38a169 0%, #2f855a 100%)",
    },
    {
      light: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      dark: "linear-gradient(135deg, #ed64a6 0%, #f6ad55 100%)",
    },
    {
      light: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      dark: "linear-gradient(135deg, #68d391 0%, #f7fafc 100%)",
    },
    {
      light: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
      dark: "linear-gradient(135deg, #f56565 0%, #f7fafc 100%)",
    },
  ];
  const color = kpiColors[colorIndex % kpiColors.length];
  const bgGradient = darkMode ? color.dark : color.light;

  const getIcon = () => {
    const iconMap = {
      salary: <MonetizationOn />,
      sales: <TrendingUp />,
      profit: <BarChart />,
      users: <Person />,
      speed: <Speed />,
    };
    const lowerTitle = title?.toLowerCase() || "";
    for (const [key, icon] of Object.entries(iconMap)) {
      if (lowerTitle.includes(key)) return icon;
    }
    return <BarChart />;
  };

  return (
    <Card
      sx={{
        minWidth: { xs: "100%", sm: 150 },
        mb: 2,
        bgcolor: "transparent",
        background: bgGradient,
        color: "#fff",
        borderRadius: 2,
        elevation: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "scale(1.02)",
          boxShadow: "0 6px 25px rgba(0,0,0,0.15)",
        },
        animation: `${fadeIn} 0.6s ease-out`,
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              mr: 1,
              p: 0.5,
              bgcolor: "rgba(255,255,255,0.2)",
              borderRadius: 1,
            }}
          >
            {getIcon()}
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {title}
          </Typography>
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            mb: 0.5,
            fontSize: { xs: "1.5rem", sm: "2rem" },
          }}
        >
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
        {trend && (
          <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
            {trend}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// SchemaEditor Component
function SchemaEditor({
  schema,
  userSessionFileId,
  fileName,
  onConfirm,
  darkMode,
}) {
  const [local, setLocal] = useState(() => ({
    columns: schema?.columns ? [...schema.columns] : [],
  }));
  const [lastConfirmed, setLastConfirmed] = useState(null);

  useEffect(() => {
    setLocal({ columns: schema?.columns ? [...schema.columns] : [] });
  }, [schema]);

  function updateColumn(i, key, value) {
    setLocal((prev) => {
      const copy = { ...prev, columns: [...prev.columns] };
      copy.columns[i] = { ...copy.columns[i], [key]: value };
      return copy;
    });
  }

  async function handleConfirm() {
    if (!userSessionFileId) return;
    const rename_col = {};
    const update_dtype = {};
    local.columns.forEach((col, i) => {
      const oldName = schema.columns[i].name;
      const oldType = schema.columns[i].detectedType;
      if (col.name !== oldName) rename_col[oldName] = col.name;
      if (col.detectedType !== oldType)
        update_dtype[col.name] = col.detectedType;
    });
    try {
      if (Object.keys(rename_col).length > 0)
        await renameColumn(fileName, userSessionFileId, rename_col);
      if (Object.keys(update_dtype).length > 0)
        await updateColumnType(fileName, userSessionFileId, update_dtype);
      setLastConfirmed(JSON.parse(JSON.stringify(local)));
      onConfirm(local);
    } catch (err) {}
  }

  if (!local.columns || local.columns.length === 0) {
    return (
      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography>No schema available to edit.</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 2,
        p: 3,
        borderRadius: 2,
        bgcolor: darkMode ? "#2d384b" : "#ffffff",
        border: `1px solid ${darkMode ? "#475569" : "#e2e8f0"}`,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Review Schema
      </Typography>
      {local.columns.map((c, i) => (
        <Box key={i} sx={{ display: "flex", gap: 2, mb: 1 }}>
          <TextField
            label="Column Name"
            value={c.name || ""}
            onChange={(e) => updateColumn(i, "name", e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
          />
          <FormControl size="small" sx={{ width: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={c.detectedType || ""}
              label="Type"
              onChange={(e) => updateColumn(i, "detectedType", e.target.value)}
            >
              {DATA_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ))}
      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Button variant="contained" color="primary" onClick={handleConfirm}>
          Confirm Schema
        </Button>
      </Box>
      {lastConfirmed && (
        <Typography sx={{ mt: 2, textAlign: "right", color: "green" }}>
          Schema confirmed! (last update applied)
        </Typography>
      )}
    </Paper>
  );
}

// Main MessageList Component
export default function MessageList({ messages, onSchemaUpdate, darkMode }) {
  const ref = useRef();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    ref.current?.scrollTo({
      top: ref.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  return (
    <Box
      ref={ref}
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 7,
      }}
    >
      {messages.map((m, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-end",
            justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            animation: `${fadeIn} 0.5s ease-out`,
          }}
        >
          {m.role === "assistant" && (
            <Avatar
              sx={{ bgcolor: "#0b74ff", width: 32, height: 32, color: "white" }}
            >
              T
            </Avatar>
          )}
          {/* <Box
            sx={{
              maxWidth: { xs: "100%", sm: "75%" },
              display: "inline-block",
            }}
          > */}
          <Box
            sx={{
              maxWidth:
                m.role === "assistant" ? "92%" : { xs: "100%", sm: "75%" },

              display: "inline-block",
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: "20px",
                bgcolor:
                  m.role === "user" ? "#0b74ff" : "rgba(128,128,128,0.1)",
                color: m.role === "user" ? "#fff" : "inherit",
                whiteSpace: "pre-line",
                width: "fit-content",
                maxWidth: "100%",
              }}
            >
              {/* {m.meta?.preface && (
                <Typography sx={{ fontWeight: "bold", fontSize: 14, mb: 1 }}>
                  Got it ✅ — {m.meta.preface}
                </Typography>
              )} */}

              {m.meta?.preface && (
                <TypingEffect
                  text={`Yes, Got it ✅ — ${m.meta.preface}`}
                  sx={{
                    fontWeight: "bold",
                    fontSize: 14,
                    mb: 1,
                    color: darkMode ? "#cbd5e1" : "#334155", // adapt to theme
                  }}
                  speed={100}
                />
              )}

              {(!m.meta?.charts || m.meta.charts.length === 0) && (
                <TypingEffect
                  text={m.text}
                  sx={{ fontSize: 14, color: "#cbd5e1", lineHeight: 1.5 }}
                  speed={100}
                />
              )}

              {/* {(!m.meta?.charts || m.meta.charts.length === 0) && (
                <Typography variant="body2">{m.text}</Typography>
              )} */}

              {m.meta?.charts && m.meta.charts.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 3,
                    alignItems: "flex-start",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {m.meta.charts.includes("table") && m.meta.table && (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 1,
                            fontWeight: "bold",
                            color: darkMode ? "#cbd5e1" : "#1e293b",
                          }}
                        >
                          Table View
                        </Typography>
                        <DataTable
                          rows={m.meta.table}
                          schema={m.meta.schema}
                          darkMode={darkMode}
                        />
                      </Box>
                    )}

                    {m.meta.charts.some(
                      (type) => type.toLowerCase() === "kpi card"
                    ) && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 2,
                          justifyContent: "flex-start",
                        }}
                      >
                        {m.meta.data?.length > 0 ? (
                          m.meta.data.map((item, index) => {
                            let title, value, trend;
                            if (item.title && item.value !== undefined) {
                              title = item.title;
                              value = item.value;
                              trend = item.trend;
                            } else {
                              const key = Object.keys(item)[0];
                              title = key || m.meta.title || "KPI";
                              value = item[key];
                              trend = null;
                            }
                            return (
                              <KpiCard
                                key={index}
                                title={title}
                                value={value}
                                trend={trend}
                                colorIndex={index}
                                darkMode={darkMode}
                              />
                            );
                          })
                        ) : (
                          <Box
                            sx={{ p: 2, color: "gray", fontStyle: "italic" }}
                          >
                            No data for KPI card.
                          </Box>
                        )}
                      </Box>
                    )}

                    {m.meta.charts.filter((type) =>
                      [
                        "table",
                        "bar",
                        "line",
                        "pie",
                        "donut",
                        "histogram",
                        "scatter",
                        "area",
                        "radar",
                        "treemap",
                        "funnel",
                        "bubble",
                        "waterfall",
                        "heatmap",
                      ].includes(type.toLowerCase())
                    ).length > 0 && (
                      <DynamicChartViewer
                        possibleCharts={m.meta.charts}
                        data={m.meta.data}
                        xAxis={m.meta.xAxis}
                        yAxis={m.meta.yAxis}
                        title={m.meta.title}
                        darkMode={darkMode}
                      />
                    )}
                  </Box>

                  {m.meta?.insights && (
                    <Paper
                      sx={{
                        flex: 0.8,
                        p: 2.5,
                        borderRadius: 2,
                        bgcolor: darkMode ? "#1e293b" : "#f1f5f9",
                        boxShadow: darkMode
                          ? "0 4px 12px rgba(255,255,255,0.1)"
                          : "0 4px 12px rgba(0,0,0,0.05)",
                        minWidth: 250,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "bold",
                          mb: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          fontSize: "1.2rem",
                          color: darkMode ? "#fff" : "#111827",
                        }}
                      >
                        <Box component="span" sx={{ fontSize: "1.5rem" }}>
                          🪶
                        </Box>
                        Insights Summary
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.2,
                        }}
                      >
                        {m.meta.insights
                          .replace(/undefined|null/gi, "")
                          .split(/(?:\r?\n)+/) // split by line breaks
                          .map((p) => p.trim())
                          .filter(Boolean)
                          .map((point, idx) => (
                            <TypingEffect
                              key={idx}
                              text={` ${point}`}
                              sx={{
                                color: darkMode ? "#cbd5e1" : "#334155",
                                lineHeight: 1.7,
                                fontSize: 14,
                              }}
                              speed={100}
                            />
                          ))}
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>

            {/* {m.meta?.charts && m.meta.charts.length > 0 && (
              <Box
                sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
              >
                {m.meta.charts.includes("table") && m.meta.table && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: "bold" }}
                    >
                      Table View
                    </Typography>
                    <DataTable
                      rows={m.meta.table}
                      schema={m.meta.schema}
                      darkMode={darkMode}
                    />
                  </Box>
                )}

                {m.meta.charts.some(
                  (type) => type.toLowerCase() === "kpi card"
                ) && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {m.meta.data?.length > 0 ? (
                      m.meta.data.map((item, index) => {
                        let title, value, trend;
                        if (item.title && item.value !== undefined) {
                          title = item.title;
                          value = item.value;
                          trend = item.trend;
                        } else {
                          const key = Object.keys(item)[0];
                          title = key || m.meta.title || "KPI";
                          value = item[key];
                          trend = null;
                        }
                        return (
                          <KpiCard
                            key={index}
                            title={title}
                            value={value}
                            trend={trend}
                            colorIndex={index}
                            darkMode={darkMode}
                          />
                        );
                      })
                    ) : (
                      <Box sx={{ p: 2, color: "gray", fontStyle: "italic" }}>
                        No data for KPI card.
                      </Box>
                    )}
                  </Box>
                )}

                {m.meta.charts.filter((type) =>
                  [
                    "table",
                    "bar",
                    "line",
                    "pie",
                    "donut",
                    "histogram",
                    "scatter",
                    "area",
                    "radar",
                    "treemap",
                    "funnel",
                    "bubble",
                    "waterfall",
                    "heatmap",
                  ].includes(type.toLowerCase())
                ).length > 0 && (
                  <DynamicChartViewer
                    possibleCharts={m.meta.charts}
                    data={m.meta.data}
                    xAxis={m.meta.xAxis}
                    yAxis={m.meta.yAxis}
                    title={m.meta.title}
                    darkMode={darkMode}
                  />
                )}
              </Box>
            )}

            {m.meta?.insights && (
              <Paper
                sx={{
                  p: 2,
                  mt: 2,
                  borderRadius: 2,
                  bgcolor: darkMode ? "#1e293b" : "#f1f5f9",
                  // borderLeft: `4px solid ${darkMode ? "#4ade80" : "#10b981"}`,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: "1.2rem",
                    color: darkMode ? "#fff" : "#111827",
                  }}
                >
                  <Box component="span" sx={{ fontSize: "1.5rem" }}>
                    🪶
                  </Box>
                  Insights Summary
                </Typography>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}
                >
                  {m.meta.insights
                    .replace(/undefined|null/gi, "")
                    .split("*")
                    .map((p) => p.trim())
                    .filter(Boolean)
                    .map((point, idx) => (
                      <TypingEffect
                        key={idx}
                        text={`• ${point}`}
                        sx={{
                          color: darkMode ? "#cbd5e1" : "#334155",
                          lineHeight: 1.7,
                          fontSize: 14,
                        }}
                        speed={100}
                      />
                    ))}
                </Box>
              </Paper>
            )} */}

            {m.meta?.schema && (
              <SchemaEditor
                schema={m.meta.schema}
                userSessionFileId={m.meta.userSessionFileId}
                fileName={m.meta.fileName}
                onConfirm={onSchemaUpdate}
                darkMode={darkMode}
              />
            )}

            {m.meta?.list && (
              <Box sx={{ mt: 1, pl: 2 }}>
                <ol style={{ margin: 0, paddingLeft: "1.5rem" }}>
                  {m.meta.list.map((val, i) => (
                    <li key={i}>
                      <Typography variant="body2">{val}</Typography>
                    </li>
                  ))}
                </ol>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => {
                    navigator.clipboard.writeText(m.meta.list.join("\n"));
                    setSnackbarOpen(true);
                  }}
                >
                  Copy All Values
                </Button>
              </Box>
            )}

            {m.meta?.summary && (
              <Paper
                sx={{
                  p: 1.5,
                  mt: 1,
                  borderRadius: 2,
                  bgcolor: darkMode ? "#1e293b" : "#f9fafb",
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                  {m.meta.summary}
                </Typography>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  sx={{ mt: 3 }}
                  onClick={() => {
                    const report =
                      typeof m.meta.summary === "string"
                        ? m.meta.summary
                        : JSON.stringify(m.meta.summary, null, 2);
                    const blob = new Blob([report], { type: "text/plain" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "dataset_report.txt";
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  Download Report
                </Button>
              </Paper>
            )}
          </Box>

          {m.role === "user" && (
            <Avatar sx={{ bgcolor: "#6366f1", width: 32, height: 32 }}></Avatar>
          )}
        </Box>
      ))}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1000}
        onClose={() => setSnackbarOpen(false)}
        message="Copied!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
